/*
 * assets/countdown.js — Boostly countdown engine (CONVENTIONS §4).
 * Replaces: jquery.countdownTimer*.js, flipclock.js, jquery.final-countdown.js (+ missing kinetic.js),
 * jquery.downCount.js and the timer snippets product_count_24m / product_count_random / set_cookies /
 * product_count_down_custom_with_cookies / reset_every_day / product_count_down_reset_every_day /
 * *_home variants, plus the progress/stock JS of product-count.liquid.
 *
 * Modes (identical semantics to the original algorithms):
 *   custom  — counts down data-seconds from page load (no persistence)            [enable_timer_custom]
 *   random  — random minutes from data-random-minutes on each load                [enable_timer_randomly]
 *   cookie  — end time stored in cookie data-cookie-key (value = end ISO, expires at end);
 *             new cycle starts automatically when the cookie expires              [enable_timer_custom_cookies]
 *   daily   — counts to next data-reset-time (HH:MM:SS, visitor local time), 24h cycle;
 *             optional reload 4s after the end (original behaviour)               [enable_timer_custom_time_revise]
 *   fixed   — data-seconds, optionally persisted in cookie data-persist-key (cart timer)
 * Stock ("HURRY! ONLY N LEFT"):
 *   custom (data-stock) | random (3–12, persisted in cookie mode) | inventory (real, constant) | inventory-desc
 *   left = max(1, ceil(stockTotal × remaining / total))  — linear decrease like the original counter.
 * Models (renderers): 1 table + labels · 2 flip clock · 3 boxed digits · 4 SVG rings · 5 day column cards · 6 dotted
 * Events (bubbling, on the element): countdown:tick {remaining,total,stock,stockTotal,parts}, countdown:end
 * Mirrors: any [data-countdown-mirror="<element id>"] receives plain "HH:MM:SS" text (sticky bar, cart).
 */
(function () {
  'use strict';
  if (customElements.get('boostly-countdown')) return;

  const pad = (n) => String(n).padStart(2, '0');
  const RANDOM_MINUTES = [10, 15, 20, 25, 30, 45, 60, 90, 120, 180, 300, 420, 600, 720];
  const RANDOM_STOCK = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  function getCookie(name) {
    if (window.Theme && Theme.cookies) return Theme.cookies.get(name);
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, expires) {
    if (window.Theme && Theme.cookies) return Theme.cookies.set(name, value, { expires });
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
  }
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  function split(sec) {
    sec = Math.max(0, Math.floor(sec));
    return { days: Math.floor(sec / 86400), hours: Math.floor((sec % 86400) / 3600), minutes: Math.floor((sec % 3600) / 60), seconds: sec % 60 };
  }

  class BoostlyCountdown extends HTMLElement {
    connectedCallback() {
      if (this._started) { // re-attached (e.g. moved during a section re-render): resume ticking
        clearInterval(this._interval);
        this._interval = setInterval(() => this.tick(), 1000);
        return;
      }
      this._started = true;
      if (!this.id) this.id = 'Countdown-' + Math.random().toString(36).slice(2, 8);
      this.mode = this.dataset.mode || 'custom';
      this.model = String(this.dataset.model || '1').replace('timer_model', '');
      try { this.labels = JSON.parse(this.dataset.labels || '{}'); } catch (e) { this.labels = {}; }
      const s = (window.Theme && Theme.strings) || {};
      this.labels = Object.assign({ day: s.timerDay || 'Days', hour: s.timerHour || 'Hours', minute: s.timerMinute || 'Minutes', second: s.timerSecond || 'Seconds' }, this.labels);
      this.startCycle();
      this.renderSkeleton();
      this.tick();
      this._interval = setInterval(() => this.tick(), 1000);
      document.addEventListener('visibilitychange', this._vis = () => { if (!document.hidden) this.tick(); });
    }
    disconnectedCallback() { clearInterval(this._interval); document.removeEventListener('visibilitychange', this._vis); }

    /* ------------------------------------------------------------ cycle */
    startCycle() {
      const now = Date.now();
      const seconds = Math.max(1, parseInt(this.dataset.seconds, 10) || 150);
      switch (this.mode) {
        case 'random': {
          const list = (this.dataset.randomMinutes || '').split(',').map(Number).filter(Boolean);
          this.total = pick(list.length ? list : RANDOM_MINUTES) * 60;
          this.end = now + this.total * 1000;
          break;
        }
        case 'cookie': {
          const key = this.dataset.cookieKey || 'product_common_id';
          this.total = seconds;
          let end = Date.parse(getCookie(key) || '');
          // FIX: original used Math.abs() so an expired-but-present cookie counted upwards.
          if (!end || isNaN(end) || end <= now || end - now > seconds * 1000) {
            end = now + seconds * 1000;
            setCookie(key, new Date(end).toISOString(), new Date(end));
            if (this.dataset.stockCookieKey) setCookie(this.dataset.stockCookieKey, '', new Date(0));
          }
          this.end = end;
          break;
        }
        case 'daily': {
          const [h, m, sec] = (this.dataset.resetTime || '10:00:00').split(':').map((v) => parseInt(v, 10) || 0);
          const target = new Date(); target.setHours(h, m, sec, 0);
          if (target.getTime() <= now) target.setDate(target.getDate() + 1);
          this.end = target.getTime();
          this.total = 86400;
          break;
        }
        case 'fixed': {
          this.total = seconds;
          const key = this.dataset.persistKey;
          let end = key ? Date.parse(getCookie(key) || '') : NaN;
          if (!end || isNaN(end) || end <= now) {
            end = now + seconds * 1000;
            if (key) setCookie(key, new Date(end).toISOString(), new Date(end));
          }
          this.end = end;
          break;
        }
        default: // custom
          this.total = seconds;
          this.end = now + seconds * 1000;
      }
      this.initStock();
      this.ended = false;
    }

    initStock() {
      const stockMode = this.dataset.stockMode || '';
      const inv = parseInt(this.dataset.inventory, 10);
      let total = parseInt(this.dataset.stock, 10) || 0;
      if (stockMode === 'random') {
        const key = this.dataset.stockCookieKey;
        let v = key && this.mode === 'cookie' ? parseInt(getCookie(key), 10) : NaN;
        if (!v) { v = pick(RANDOM_STOCK); if (key && this.mode === 'cookie') setCookie(key, v, new Date(this.end)); }
        total = v;
      } else if (stockMode === 'inventory' || stockMode === 'inventory-desc') {
        total = inv > 0 ? inv : 1;
      }
      this.stockMode = stockMode;
      this.stockTotal = total;
    }

    stockLeft(remaining) {
      if (!this.stockTotal) return 0;
      if (this.stockMode === 'inventory') return this.stockTotal;
      // CONVENTIONS §4 / original set_cookies.liquid: nowLeftnumber = diffDays / (total/stockTotal), rounded (not ceil).
      return Math.max(1, Math.round(this.stockTotal * (remaining / this.total)));
    }

    /* ------------------------------------------------------------ tick */
    tick() {
      let remaining = Math.max(0, Math.round((this.end - Date.now()) / 1000));
      if (remaining <= 0 && !this.ended) {
        this.ended = true;
        this.dispatchEvent(new CustomEvent('countdown:end', { bubbles: true, detail: { mode: this.mode } }));
        if (this.mode === 'cookie' || this.mode === 'daily') {
          if (this.mode === 'daily' && this.dataset.reloadOnEnd === 'true' && !(window.Theme && Theme.settings && Theme.settings.designMode)) {
            setTimeout(() => window.location.reload(), 4000);
          }
          this.startCycle();
          remaining = Math.max(0, Math.round((this.end - Date.now()) / 1000));
        }
      }
      const parts = split(remaining);
      this.update(parts, remaining);
      const stock = this.stockLeft(remaining);
      this.dispatchEvent(new CustomEvent('countdown:tick', { bubbles: true, detail: { remaining, total: this.total, stock, stockTotal: this.stockTotal, parts, id: this.id } }));
      const text = (parts.days ? parts.days + ':' : '') + `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
      document.querySelectorAll(`[data-countdown-mirror="${this.id}"]`).forEach((el) => { el.textContent = text; });
      this.setAttribute('aria-label', text);
    }

    get showDays() {
      return ['4', '5', '6'].includes(this.model) || this.mode === 'cookie' || this.mode === 'daily' || this.total >= 86400;
    }

    /* ------------------------------------------------------------ render */
    renderSkeleton() {
      const units = this.showDays ? ['days', 'hours', 'minutes', 'seconds'] : ['hours', 'minutes', 'seconds'];
      const L = { days: this.labels.day, hours: this.labels.hour, minutes: this.labels.minute, seconds: this.labels.second };
      this.units = units;
      this.classList.add('countdown', `countdown--model-${this.model}`);
      this.setAttribute('role', 'timer');
      let html = '';
      switch (this.model) {
        case '2': // flip clock
          html = `<div class="cd-flip">${units.map((u, i) => `
            <div class="cd-flip__group" data-unit="${u}">
              <div class="cd-flip__digits"><span class="cd-flip__card" data-d="0"><span>0</span></span><span class="cd-flip__card" data-d="1"><span>0</span></span></div>
              <span class="cd-flip__label">${L[u]}</span>
            </div>${i < units.length - 1 ? '<span class="cd-flip__sep">:</span>' : ''}`).join('')}</div>`;
          break;
        case '3': // boxed digits with labels under each
          html = `<div class="cd-boxes">${units.map((u) => `<div class="cd-box" data-unit="${u}"><span class="cd-box__value">00</span><span class="cd-box__label">${L[u]}</span></div>`).join('')}</div>`;
          break;
        case '4': { // SVG circular rings
          const r = 40, c = 2 * Math.PI * r;
          this._circ = c;
          html = `<div class="cd-rings">${['days', 'hours', 'minutes', 'seconds'].map((u) => `
            <div class="cd-ring" data-unit="${u}">
              <svg viewBox="0 0 100 100" aria-hidden="true"><circle class="cd-ring__track" cx="50" cy="50" r="${r}"/><circle class="cd-ring__bar" cx="50" cy="50" r="${r}" stroke-dasharray="${c}" stroke-dashoffset="0"/></svg>
              <div class="cd-ring__text"><span class="cd-ring__value">00</span><span class="cd-ring__label">${L[u]}</span></div>
            </div>`).join('')}</div>`;
          this.units = ['days', 'hours', 'minutes', 'seconds'];
          break;
        }
        case '5': // day column + samp digit cards
          this.units = ['days', 'hours', 'minutes', 'seconds'];
          html = `<table class="cd-table cd-table--model5"><tr class="cd-table__values">${this.units.map((u, i) => `<td data-unit="${u}"><samp class="cd-value">00</samp></td>${i < 3 ? '<td class="cd-table__sep"><span></span></td>' : ''}`).join('')}</tr>
            <tr class="cd-table__labels">${this.units.map((u, i) => `<td>${L[u]}</td>${i < 3 ? '<td></td>' : ''}`).join('')}</tr></table>`;
          break;
        case '6': // dotted separators
          this.units = ['days', 'hours', 'minutes', 'seconds'];
          html = `<table class="cd-table cd-table--model6"><tr class="cd-table__values">${this.units.map((u, i) => `<td data-unit="${u}"><span class="cd-value">00</span></td>${i < 3 ? '<td class="cd-table__sep"><i>..</i></td>' : ''}`).join('')}</tr>
            <tr class="cd-table__labels">${this.units.map((u, i) => `<td>${L[u]}</td>${i < 3 ? '<td></td>' : ''}`).join('')}</tr></table>`;
          break;
        default: // model 1: digits row + labels row
          html = `<table class="cd-table cd-table--model1"><tr class="cd-table__values"><td colspan="${units.length * 2 - 1}">${units.map((u, i) => `<samp class="cd-value" data-unit="${u}">00</samp>${i < units.length - 1 ? '<span class="cd-colon">:</span>' : ''}`).join('')}</td></tr>
            <tr class="cd-table__labels">${units.map((u, i) => `<td><span class="cd-label">${L[u]}</span></td>${i < units.length - 1 ? '<td class="cd-sep"></td>' : ''}`).join('')}</tr></table>`;
      }
      const target = this.querySelector('[data-countdown-render]') || this;
      target.innerHTML = html;
      this._target = target;
    }

    update(p, remaining) {
      const t = this._target;
      if (!this.showDays && this.units.indexOf('days') === -1) p = Object.assign({}, p, { hours: p.hours + p.days * 24 });
      if (this.model === '2') {
        this.units.forEach((u) => {
          const g = t.querySelector(`.cd-flip__group[data-unit="${u}"]`); if (!g) return;
          const v = pad(p[u]).slice(-2);
          g.querySelectorAll('.cd-flip__card').forEach((card, i) => {
            const d = v[i]; const span = card.querySelector('span');
            if (span.textContent !== d) {
              span.textContent = d;
              card.classList.remove('is-flipping'); void card.offsetWidth; card.classList.add('is-flipping');
            }
          });
        });
        return;
      }
      if (this.model === '4') {
        const max = { days: Math.max(1, Math.ceil(this.total / 86400)), hours: 24, minutes: 60, seconds: 60 };
        this.units.forEach((u) => {
          const ring = t.querySelector(`.cd-ring[data-unit="${u}"]`); if (!ring) return;
          ring.querySelector('.cd-ring__value').textContent = pad(p[u]);
          const ratio = Math.min(1, p[u] / max[u]);
          ring.querySelector('.cd-ring__bar').setAttribute('stroke-dashoffset', String(this._circ * (1 - ratio)));
        });
        return;
      }
      this.units.forEach((u) => {
        t.querySelectorAll(`[data-unit="${u}"] .cd-value, .cd-value[data-unit="${u}"], [data-unit="${u}"] .cd-box__value`).forEach((el) => { el.textContent = pad(p[u]); });
      });
    }
  }
  customElements.define('boostly-countdown', BoostlyCountdown);

  /* <boostly-stock-bar data-countdown="<id>" data-stock-mode data-stock> — "HURRY! ONLY N LEFT" + progress bar.
     Replaces element_progress_bar.liquid + the decrease()/progress() JS of product-count.liquid. */
  class BoostlyStockBar extends HTMLElement {
    connectedCallback() {
      this.count = this.querySelector('[data-stock-count]');
      this.bar = this.querySelector('[data-progress-bar]');
      this.track = this.bar && this.bar.closest('[role="progressbar"]');
      const id = this.dataset.countdown;
      // FIX: a blank/absent data-countdown must mean "no countdown present" → stay static
      // (previously a falsy `id` matched every countdown's tick event on the page).
      this._onTick = (e) => {
        if (!id || e.detail.id !== id) return;
        const { remaining, total, stock, stockTotal } = e.detail;
        if (this.count && stockTotal) this.count.textContent = stock;
        if (this.bar) {
          let pct = total ? (remaining / total) * 100 : 100;
          if (this.dataset.stockMode === 'inventory') pct = Math.min(100, (stock / 50) * 100); // original: left × 2.7px of 135px
          pct = Math.max(2, pct);
          this.bar.style.width = pct.toFixed(2) + '%';
          if (this.track) this.track.setAttribute('aria-valuenow', String(Math.round(pct)));
        }
      };
      document.addEventListener('countdown:tick', this._onTick);
    }
    disconnectedCallback() { document.removeEventListener('countdown:tick', this._onTick); }
  }
  if (!customElements.get('boostly-stock-bar')) customElements.define('boostly-stock-bar', BoostlyStockBar);
})();
